// Transactions (SM-755): search stored money operations by transaction
// id, request id, user, operation, reason, status and received time.
// Filters live in the URL so a search is shareable.
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { TransactionsTable } from "@/components/domain/TransactionsTable";
import { Button } from "@/components/ui/Button";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { Field, Select, TextInput } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/Misc";
import { Skeleton } from "@/components/ui/Skeleton";
import { OPERATIONS, REASONS, TXN_STATUSES } from "@/lib/constants";
import { localToRfc3339, rfc3339ToLocal } from "@/lib/format";
import { fetchers, keys } from "@/lib/queries";

const FIELDS = ["transaction_id", "request_id", "user_id", "operation", "reason", "status", "from", "to"] as const;
type TxnSearch = Partial<Record<(typeof FIELDS)[number], string>>;

export const Route = createFileRoute("/_app/transactions/")({
  validateSearch: (s: Record<string, unknown>): TxnSearch => {
    const out: TxnSearch = {};
    for (const f of FIELDS) if (typeof s[f] === "string" && s[f]) out[f] = s[f] as string;
    return out;
  },
  component: TransactionsPage,
});

function TransactionsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/transactions/" });
  const [draft, setDraft] = useState<TxnSearch>(search);
  const txns = useQuery({ queryKey: keys.transactions(search), queryFn: () => fetchers.transactions(search) });
  const set = (k: keyof TxnSearch) => (v: string) => setDraft((d) => ({ ...d, [k]: v || undefined }));

  return (
    <div>
      <PageHeader title="Transactions" subtitle="Every Deduct, Credit and Rollback the mock stored. A transaction id matches the transaction and the Rollback that names it as original." />
      <form
        className="card mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          const clean: TxnSearch = {};
          for (const f of FIELDS) if (draft[f]) clean[f] = draft[f]!.trim();
          navigate({ search: clean });
        }}
      >
        <div className="form-grid">
          <Field label="Transaction id">
            <TextInput value={draft.transaction_id ?? ""} onChange={(e) => set("transaction_id")(e.target.value)} className="mono" />
          </Field>
          <Field label="Request id">
            <TextInput value={draft.request_id ?? ""} onChange={(e) => set("request_id")(e.target.value)} className="mono" />
          </Field>
          <Field label="User id">
            <TextInput value={draft.user_id ?? ""} onChange={(e) => set("user_id")(e.target.value)} className="mono" />
          </Field>
          <Field label="Operation">
            <Select value={draft.operation ?? ""} onChange={(e) => set("operation")(e.target.value)}>
              <option value="">Any</option>
              {OPERATIONS.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </Select>
          </Field>
          <Field label="Reason">
            <Select value={draft.reason ?? ""} onChange={(e) => set("reason")(e.target.value)}>
              <option value="">Any</option>
              {REASONS.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </Select>
          </Field>
          <Field label="Status">
            <Select value={draft.status ?? ""} onChange={(e) => set("status")(e.target.value)}>
              <option value="">Any</option>
              {TXN_STATUSES.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </Select>
          </Field>
          <Field label="Received from">
            <TextInput type="datetime-local" step="1" value={rfc3339ToLocal(draft.from)} onChange={(e) => set("from")(localToRfc3339(e.target.value) ?? "")} />
          </Field>
          <Field label="Received before">
            <TextInput type="datetime-local" step="1" value={rfc3339ToLocal(draft.to)} onChange={(e) => set("to")(localToRfc3339(e.target.value) ?? "")} />
          </Field>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setDraft({});
              navigate({ search: {} });
            }}
          >
            Clear
          </Button>
          <Button type="submit">Search</Button>
        </div>
      </form>
      <ErrorBanner error={txns.error} />
      {txns.isLoading ? <Skeleton lines={6} /> : <TransactionsTable rows={txns.data ?? []} emptyText="No transactions match." />}
    </div>
  );
}
